// /billing/checkout — тонкий переход в оплату биллинга экосистемы.
//
// Прежний трёхшаговый мастер (тариф → способ оплаты → подтверждение) снят
// намеренно: после переезда продажи выбор провайдера, карта и подтверждение
// живут на стороне IEC ID, и рисовать их у себя значило бы показывать экран,
// который ничего не решает. Адрес оставлен рабочим — на него ведут старые
// ссылки и закладки: он берёт уровень с периодом из адреса и сразу уводит
// человека в их оформление.
//
// Без параметров показывать нечего — отправляем на экран подписки, где есть
// и состояние, и апселл.

import { tolerantSearch } from "@studyhub/shared/utils/tolerant-search";
import { Button } from "@studyhub/ui/components/button";
import { IconLoader2 } from "@tabler/icons-react";
import { createFileRoute, Link, redirect } from "@tanstack/react-router";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { useIecCheckout } from "@/components/billing/_use-iec-checkout";
import { isCommerceVisible } from "@/lib/module-availability/commerce";
import { api } from "@/lib/trpc-client";

const searchSchema = z.object({
	tier: z.enum(["plus", "pro"]).optional(),
	/** Период их прайса. Прежнее локальное `annual` наружу больше не ходит. */
	period: z.enum(["monthly", "yearly"]).default("monthly"),
});

export const Route = createFileRoute("/billing/checkout")({
	validateSearch: tolerantSearch(searchSchema),
	beforeLoad: ({ search }) => {
		// Гейт коммерции стоит ПЕРВЫМ: когда продажа скрыта, оплата не должна
		// открываться даже по прямой ссылке, и уводить надо на главную, а не на
		// `/settings/billing` — тот роут при выключенной коммерции сам закрыт.
		if (!isCommerceVisible()) throw redirect({ to: "/" });
		// Zod уже отсеял бы мусор, но адрес без `tier` — законная ситуация
		// (старая закладка на голый `/billing/checkout`).
		if (!search.tier) throw redirect({ to: "/settings/billing", search: {} });
	},
	component: CheckoutRedirectPage,
});

function CheckoutRedirectPage() {
	const { t } = useTranslation("billing");
	const { tier = "plus", period } = Route.useSearch();
	const entitlements = api.iecId.entitlements.get.useQuery(undefined, { staleTime: 30_000 });
	const checkout = useIecCheckout(
		entitlements.data
			? { tier: entitlements.data.tier, serviceTier: entitlements.data.serviceTier }
			: undefined,
	);

	// Один запрос на заход: код перехода одноразовый, а повторный вызов на
	// каждый ререндер сжёг бы их лимит и оставил человека с мёртвой ссылкой.
	const startedRef = useRef(false);
	const start = checkout.startCheckout;
	useEffect(() => {
		if (startedRef.current || entitlements.isLoading) return;
		startedRef.current = true;
		start({ tier, period });
	}, [entitlements.isLoading, period, start, tier]);

	return (
		<div className="flex min-h-dvh flex-col items-center justify-center bg-muted/30 px-4">
			<div className="flex w-full max-w-sm flex-col items-center gap-3 rounded-[var(--radius-panel)] border border-border bg-card p-8 text-center shadow-e2">
				<IconLoader2 size={32} className="animate-spin text-primary" />
				<h1 className="text-lg font-semibold text-foreground">{t("checkout.processing")}</h1>
				<Button asChild variant="ghost" size="sm" className="mt-2 cursor-pointer">
					<Link to="/settings/billing" search={{}}>
						{t("checkout.cancel")}
					</Link>
				</Button>
			</div>
		</div>
	);
}
